import { t } from '@lingui/macro'
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { AspenPatientsService, PatientDTO } from '../client'

interface PatientSearchDropdownProps {
    value: PatientDTO | null
    onChange: (patient: PatientDTO | null) => void
    label?: string
    placeholder?: string
    disabled?: boolean
    error?: boolean
    helperText?: string
}

export const PatientSearchDropdown: React.FC<PatientSearchDropdownProps> = ({
    value,
    onChange,
    label = t`Assigned Patient`,
    placeholder = t`Search patients by name, DOB, or phone...`,
    disabled = false,
    error = false,
    helperText
}) => {
    const [inputValue, setInputValue] = useState('')
    const [options, setOptions] = useState<PatientDTO[]>([])
    const [loading, setLoading] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [allPatients, setAllPatients] = useState<PatientDTO[]>([])
    const [hasLoadedAllPatients, setHasLoadedAllPatients] = useState(false)
    const [searchIndex, setSearchIndex] = useState<Map<string, PatientDTO[]>>(new Map())

    // Sort results by relevance (exact matches first, then partial matches)
    const sortByRelevance = useCallback((patients: PatientDTO[], query: string) => {
        const searchTerm = query.toLowerCase()

        return patients.sort((a, b) => {
            const aFirstName = a.firstName?.toLowerCase() || ''
            const aLastName = a.lastName?.toLowerCase() || ''
            const bFirstName = b.firstName?.toLowerCase() || ''
            const bLastName = b.lastName?.toLowerCase() || ''

            // Exact matches get highest priority
            const aExact = aFirstName === searchTerm || aLastName === searchTerm
            const bExact = bFirstName === searchTerm || bLastName === searchTerm

            if (aExact && !bExact) return -1
            if (!aExact && bExact) return 1

            // Then by starts with
            const aStartsWith = aFirstName.startsWith(searchTerm) || aLastName.startsWith(searchTerm)
            const bStartsWith = bFirstName.startsWith(searchTerm) || bLastName.startsWith(searchTerm)

            if (aStartsWith && !bStartsWith) return -1
            if (!aStartsWith && bStartsWith) return 1

            // Finally by alphabetical order
            return (aFirstName + ' ' + aLastName).localeCompare(bFirstName + ' ' + bLastName)
        })
    }, [])

    // Build search index for faster searches
    const buildSearchIndex = useCallback((patients: PatientDTO[]) => {
        const index = new Map<string, PatientDTO[]>()

        // Index by first name
        patients.forEach((patient) => {
            if (patient.firstName) {
                const key = patient.firstName.toLowerCase()
                if (!index.has(key)) index.set(key, [])
                index.get(key)!.push(patient)
            }
        })

        // Index by last name
        patients.forEach((patient) => {
            if (patient.lastName) {
                const key = patient.lastName.toLowerCase()
                if (!index.has(key)) index.set(key, [])
                index.get(key)!.push(patient)
            }
        })

        // Index by phone (partial matches)
        patients.forEach((patient) => {
            if (patient.mobilePhone) {
                const phone = patient.mobilePhone.replace(/\D/g, '')
                for (let i = 3; i <= phone.length; i++) {
                    const key = phone.substring(0, i)
                    if (!index.has(key)) index.set(key, [])
                    index.get(key)!.push(patient)
                }
            }
        })

        setSearchIndex(index)
        console.log(t`Search index built with`, index.size, t`keys`)
    }, [])

    // Smart search using pre-built index and fuzzy matching
    const performSearch = useCallback(
        (query: string, patients: PatientDTO[]) => {
            if (!query.trim()) return patients.slice(0, 50)

            const searchTerm = query.toLowerCase().trim()
            const results = new Set<PatientDTO>()

            // 1. Exact matches from search index (fastest)
            if (searchIndex.has(searchTerm)) {
                searchIndex.get(searchTerm)!.forEach((patient) => results.add(patient))
            }

            // 2. Partial matches from search index
            for (const [key, patientList] of searchIndex.entries()) {
                if (key.includes(searchTerm) || searchTerm.includes(key)) {
                    patientList.forEach((patient) => results.add(patient))
                }
            }

            // 3. Fuzzy search for remaining patients
            patients.forEach((patient) => {
                const firstName = patient.firstName?.toLowerCase() || ''
                const lastName = patient.lastName?.toLowerCase() || ''
                const phone = patient.mobilePhone?.replace(/\D/g, '') || ''
                const dob = patient.dateOfBirth || ''

                // Check if any field contains the search term
                if (firstName.includes(searchTerm) || lastName.includes(searchTerm) || phone.includes(searchTerm) || dob.includes(searchTerm)) {
                    results.add(patient)
                }
            })

            // Convert Set to Array and sort by relevance
            const resultsArray = Array.from(results)
            return sortByRelevance(resultsArray, searchTerm)
        },
        [searchIndex, sortByRelevance]
    )

    // Load initial patients when component mounts (small set for quick display)
    useEffect(() => {
        const loadInitialPatients = async () => {
            setLoading(true)
            try {
                const response = await AspenPatientsService.indexPatientsGet({
                    page: 1,
                    limit: 25, // Increased for better initial experience
                    sort: 'first_name:asc'
                })
                const patients = response.data || []
                setOptions(patients)
                console.log(t`Initial patients loaded:`, patients.length)
            } catch (error) {
                console.error(t`Error loading initial patients:`, error)
                setOptions([])
            } finally {
                setLoading(false)
            }
        }

        loadInitialPatients()
    }, [])

    // Load all patients in background for efficient search (only once)
    useEffect(() => {
        const loadAllPatients = async () => {
            if (hasLoadedAllPatients) return

            try {
                console.log(t`Loading all patients for search...`)
                const response = await AspenPatientsService.indexPatientsGet({
                    page: 1,
                    limit: 1000, // Get all patients for efficient search
                    sort: 'first_name:asc'
                })
                const patients = response.data || []
                setAllPatients(patients)
                setHasLoadedAllPatients(true)

                // Pre-build search index for faster searches
                buildSearchIndex(patients)

                console.log(t`All patients loaded for search:`, patients.length)
            } catch (error) {
                console.error(t`Error loading all patients for search:`, error)
            }
        }

        // Load all patients after initial load with shorter delay
        const timer = setTimeout(loadAllPatients, 500) // Reduced from 1000ms
        return () => clearTimeout(timer)
    }, [hasLoadedAllPatients, buildSearchIndex])

    // Debounce search input with shorter delay for better responsiveness
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchQuery(inputValue)
        }, 200) // Reduced from 300ms for faster response

        return () => {
            clearTimeout(timer)
        }
    }, [inputValue])

    // Efficient search using loaded data
    useEffect(() => {
        if (!hasLoadedAllPatients) return

        console.log(t`Searching for patients with query:`, searchQuery)

        const startTime = performance.now()
        const filteredPatients = performSearch(searchQuery, allPatients)
        const endTime = performance.now()

        console.log(t`Search completed in`, (endTime - startTime).toFixed(2), t`ms`)
        console.log(t`Search results:`, filteredPatients.length, t`patients found`)

        setOptions(filteredPatients)
    }, [searchQuery, allPatients, hasLoadedAllPatients, performSearch])

    // Show relevant patients when search is cleared
    useEffect(() => {
        if (!searchQuery && hasLoadedAllPatients) {
            // Show most recently updated patients when no search
            const recentPatients = [...allPatients]
                .sort((a, b) => new Date(b.updatedDttm || 0).getTime() - new Date(a.updatedDttm || 0).getTime())
                .slice(0, 50)
            setOptions(recentPatients)
        }
    }, [searchQuery, allPatients, hasLoadedAllPatients])

    const getOptionLabel = (option: PatientDTO) => {
        const firstName = option.firstName || t`Unknown`
        const lastName = option.lastName || t`Unknown`
        const name = `${firstName} ${lastName}`
        const dob = option.dateOfBirth ? ` (${option.dateOfBirth})` : ''
        const phone = option.mobilePhone ? ` - ${option.mobilePhone}` : ''
        return `${name}${dob}${phone}`
    }

    const isOptionEqualToValue = (option: PatientDTO, value: PatientDTO | null) => {
        return option.id === value?.id
    }

    return (
        <Autocomplete
            value={value}
            onChange={(_, newValue) => onChange(newValue)}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => {
                console.log(t`Input changed to:`, newInputValue)
                setInputValue(newInputValue)
            }}
            options={options}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            loading={loading}
            disabled={disabled}
            filterOptions={(x) => x} // Disable built-in filtering since we handle it manually
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    error={error}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color='inherit' size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        )
                    }}
                />
            )}
            renderOption={(props, option) => (
                <Box component='li' {...props}>
                    <Box>
                        <Typography variant='body1'>
                            {option.firstName || t`Unknown`} {option.lastName || t`Unknown`}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                            {t`DOB:`} {option.dateOfBirth || t`N/A`} • {t`Phone:`} {option.mobilePhone || t`N/A`}
                        </Typography>
                    </Box>
                </Box>
            )}
            noOptionsText={loading ? t`Loading...` : t`No patients found`}
        />
    )
}
